import { DecimalPipe, isPlatformBrowser } from '@angular/common';
import {
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  makeEnvironmentProviders,
  PLATFORM_ID,
  provideAppInitializer,
} from '@angular/core';
import {
  APP_SETTINGS_SERVICE_ENDPOINT_TOKEN,
  AppConfig,
  SharedAppSettingsService,
} from '@luggagehero/shared/app-settings/data-access';
import { Config } from '@luggagehero/shared/environment';
import {
  BOOKING_TRANSFORMATION_SERVICE,
  DISCOUNT_TRANSFORMER_SERVICE,
  MOMENT_SERVICE,
  PAYMENT_METHOD_TRANSFORMER_SERVICE,
  PRICING_SERVICE,
  TIME_INTERVAL_TRANSFORMER_SERVICE,
  TRANSLATE_SERVICE,
} from '@luggagehero/shared/interfaces';
import { SHARED_APP_EVENT_SERVICE_CONFIG_TOKEN } from '@luggagehero/shared/services/app-events';
import { SharedBookingTransformationService } from '@luggagehero/shared/services/booking-transformation';
import { SharedDiscountTransformerService } from '@luggagehero/shared/services/discount-transformer';
import { SHARED_GEOCODING_SERVICE_CONFIG_TOKEN } from '@luggagehero/shared/services/geocoding';
import { HOLIDAY_SERVICE_ENDPOINT_TOKEN, SharedHolidayService } from '@luggagehero/shared/services/holiday';
import { SHARED_HTTP_SERVICE_CONFIG_TOKEN } from '@luggagehero/shared/services/http';
import { USERS_IP_LOCATION_SERVICE_ENDPOINT_TOKEN } from '@luggagehero/shared/services/ip-lookup';
import { LOCATIONS_SERVICE_ENDPOINT_TOKEN } from '@luggagehero/shared/services/locations';
import { ConsoleService, LOG_PROVIDERS, SharedLoggingService } from '@luggagehero/shared/services/logging';
import { SharedMomentService } from '@luggagehero/shared/services/moment';
import { LegacyOrderService, ORDER_SERVICE_ENDPOINT_TOKEN } from '@luggagehero/shared/services/orders';
import { SharedPaymentMethodTransformerService } from '@luggagehero/shared/services/payment-method-transformer';
import { DISCOUNT_SERVICE_ENDPOINT_TOKEN, PAYMENT_SERVICE_ENDPOINT_TOKEN } from '@luggagehero/shared/services/payments';
import { SharedPricingService } from '@luggagehero/shared/services/pricing';
import { STORAGE_LOCATIONS_SERVICE_ENDPOINT_TOKEN } from '@luggagehero/shared/services/shops';
import { SHARED_STORAGE_SERVICE_CONFIG, SharedStorageService } from '@luggagehero/shared/services/storage';
import { SharedTimeIntervalTransformerService } from '@luggagehero/shared/services/time-interval-transformer';
import {
  createTranslateLoader,
  createTranslateParser,
  SharedTranslateService,
  SharedTranslationsService,
  TRANSLATE_CONFIG,
  TranslateConfig,
} from '@luggagehero/shared/services/translation';
import { VAT_NUMBER_SERVICE_ENDPOINT_TOKEN } from '@luggagehero/shared/services/vat-number';
import { WindowPlatformService } from '@luggagehero/shared/services/window';
import { TranslateLoader, TranslateModule, TranslateParser } from '@ngx-translate/core';
import { Angulartics2Module } from 'angulartics2';
import { ToastrModule } from 'ngx-toastr';

// Factory for shared storage config.
//
// The auth token is stored in a cookie scoped to this `domain`. For the session
// to carry from the landing app (landing.<base>) to the traveler app
// (app.<base>) after a booking, the cookie MUST be set on the shared registrable
// domain — otherwise the browser rejects it (you can't set a cookie for a domain
// you're not on) and the traveler lands on the app logged out. That base differs
// per environment: `devheroo.com` on staging, `luggagehero.com` on production —
// so it can't be hardcoded. Derive it from TRAVELER_UI's host (the app the
// booking hands off to): the last two labels are the registrable domain, which a
// leading dot shares across all its subdomains.
export function getStorageServiceConfig() {
  return {
    domain: Config.isDevelopment ? 'localhost' : registrableDomainFromUrl(Config.environment.TRAVELER_UI),
  };
}

/** Registrable domain (last two labels) of a URL, e.g. https://app.devheroo.com → devheroo.com. */
function registrableDomainFromUrl(url: string): string {
  try {
    return new URL(url).hostname.split('.').slice(-2).join('.');
  } catch {
    return 'luggagehero.com';
  }
}

/**
 * Booking/payment service wiring shared by any app that hosts the booking wizard
 * (its store + confirm step) — currently `web-traveler` and `landing`.
 *
 * These providers bind the data/endpoint tokens and service interfaces the wizard
 * needs, all reading `Config.environment.TRAVELER_API`. App-specific concerns
 * (Ionic, routing, service worker, app initializer, languages, analytics) are NOT
 * part of this stack and stay in each app's own bootstrap.
 */
export interface BookingStackOptions {
  /**
   * Whether the stack bootstraps app-settings, pricing and translations itself (default `true`).
   *
   * Pass `false` from a host that does its own bootstrap (web-traveler). `SharedAppSettingsService.init()`
   * is first-caller-wins, and the stack's initializer calls it synchronously with appName `'landing'`,
   * while a host initializer that awaits anything first (web-traveler awaits `windowService.init()`)
   * reaches its own `init()` too late. Registration order does not help: Angular starts every app
   * initializer before awaiting any of them. The host then runs on the landing settings row, which
   * drops every web-traveler setting, `IS_PAYPAL_ENABLED` among them (AM-4913).
   */
  bootstrap?: boolean;
}

export function provideBookingStack(options: BookingStackOptions = {}): EnvironmentProviders {
  const { bootstrap = true } = options;
  return makeEnvironmentProviders([
    // Platform tokens the shared services layer expects (normally supplied by
    // the traveler app's xplat CoreModule.forRoot). On the browser they resolve to
    // the raw globals; on the server (SSR/prerender) `window` is undefined, so a
    // bare `() => window` throws at injector hydration — which previously broke
    // server-rendering any landing page that pulls in this stack (AM-5037). Under
    // SSR, hand back a safe stand-in instead: WindowPlatformService is a no-arg
    // class of inert defaults/no-ops with the exact shape consumers expect, and
    // the Node `console` is a valid Console. The wizard itself is browser-only
    // (its APP_INITIALIZER below is `isPlatformBrowser`-gated), so nothing on the
    // server actually calls into these — they just need to resolve without throwing.
    {
      provide: WindowPlatformService,
      useFactory: () =>
        isPlatformBrowser(inject(PLATFORM_ID))
          ? (window as unknown as WindowPlatformService)
          : new WindowPlatformService(),
    },
    { provide: ConsoleService, useFactory: () => console },
    // Logging targets (LOGGER_TOKEN bindings) that SharedLoggingService needs —
    // same set the traveler app's CoreModule spreads in.
    ...LOG_PROVIDERS,
    // angulartics2 full setup (SharedAnalyticsService -> Angulartics2). forRoot
    // provides the ANGULARTICS2 config token, RouterlessTracking and the
    // settings together — the traveler app gets this via its AnalyticsModule.
    importProvidersFrom(Angulartics2Module.forRoot()),
    // ToastrService (ngx-toastr) backs SharedNotificationService, which the
    // confirm step's payment-method selection injects — without it the payment
    // step throws NG0201. We register ONLY ToastrModule.forRoot(), not the whole
    // SharedNotificationModule: that module also pulls in BrowserAnimationsModule,
    // which would clash with a host that configures animations differently (the
    // landing app uses provideAnimationsAsync()). SharedNotificationService is
    // providedIn:'root', so it resolves once ToastrService exists.
    importProvidersFrom(ToastrModule.forRoot()),
    // The confirm step injects Angular's DecimalPipe directly (to format the
    // running total); it isn't providedIn:'root', so the host must register it —
    // web-traveler does the same in its core module. Without it the payment step
    // throws NG0201 for _DecimalPipe.
    DecimalPipe,
    // SharedHolidayService is @Injectable() (not providedIn:'root'), so it must
    // be registered explicitly for SharedShopsService to resolve it.
    SharedHolidayService,
    // LegacyOrderService is @Injectable() (not providedIn:'root') and is injected
    // by SharedBookingService — register it explicitly too.
    LegacyOrderService,
    // Provide shared storage config dynamically based on the environment
    {
      provide: SHARED_STORAGE_SERVICE_CONFIG,
      useFactory: getStorageServiceConfig,
      deps: [],
    },
    {
      provide: SHARED_HTTP_SERVICE_CONFIG_TOKEN,
      useFactory: () => {
        return {
          usesLegacyHttpMode: () => {
            return AppConfig.LEGACY_HTTP_MODE;
          },
        };
      },
    },
    {
      provide: SHARED_GEOCODING_SERVICE_CONFIG_TOKEN,
      useFactory: () => {
        return {
          apiURL: () => Config.environment.TRAVELER_API,
        };
      },
    },
    {
      provide: SHARED_APP_EVENT_SERVICE_CONFIG_TOKEN,
      useFactory: () => {
        return {
          apiURL: () => `${Config.environment.TRAVELER_API}/v2`,
        };
      },
    },
    {
      provide: HOLIDAY_SERVICE_ENDPOINT_TOKEN,
      useValue: Config.environment.TRAVELER_API,
    },
    {
      provide: APP_SETTINGS_SERVICE_ENDPOINT_TOKEN,
      useValue: Config.environment.TRAVELER_API,
    },
    {
      provide: VAT_NUMBER_SERVICE_ENDPOINT_TOKEN,
      useValue: Config.environment.TRAVELER_API,
    },
    {
      provide: LOCATIONS_SERVICE_ENDPOINT_TOKEN,
      useValue: Config.environment.TRAVELER_API,
    },
    {
      provide: STORAGE_LOCATIONS_SERVICE_ENDPOINT_TOKEN,
      useValue: `${Config.environment.TRAVELER_API}/v2/storage_locations`,
    },
    {
      provide: DISCOUNT_SERVICE_ENDPOINT_TOKEN,
      useValue: `${Config.environment.TRAVELER_API}/discounts`,
    },
    {
      provide: PAYMENT_SERVICE_ENDPOINT_TOKEN,
      useValue: `${Config.environment.TRAVELER_API}/payments`,
    },
    {
      provide: ORDER_SERVICE_ENDPOINT_TOKEN,
      useValue: `${Config.environment.TRAVELER_API}/orders`,
    },
    {
      provide: USERS_IP_LOCATION_SERVICE_ENDPOINT_TOKEN,
      useValue: Config.environment.TRAVELER_API,
    },
    {
      provide: TRANSLATE_CONFIG,
      useFactory: () => {
        return {
          appName: 'web-traveler',
          apiAddress: Config.environment.TRAVELER_API,
        } as TranslateConfig;
      },
    },
    // ngx-translate wiring: provides @ngx-translate/core's TranslateService (which
    // SharedTranslateService injects) with the same loader/parser factories as web-traveler's
    // core.module.ts / MultilingualModule, so the ngx TranslateService is injectable in any app
    // that hosts the booking wizard (e.g. landing). This mirrors what MultilingualModule imports
    // internally (TranslateModule.forRoot with the shared loader/parser + per-key 'en' fallback);
    // importing that legacy module directly is not possible here — it lives outside this buildable
    // lib's rootDir — so the equivalent TranslateModule.forRoot is inlined instead.
    importProvidersFrom(
      TranslateModule.forRoot({
        // Per-key fallback: a key with no value in the active language resolves to its English
        // copy instead of rendering raw (matches MultilingualModule; AM-4589).
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [SharedTranslationsService, SharedAppSettingsService, SharedStorageService],
        },
        parser: {
          provide: TranslateParser,
          useFactory: createTranslateParser,
          deps: [SharedTranslationsService, SharedLoggingService],
        },
      }),
    ),
    SharedTranslateService,
    { provide: TRANSLATE_SERVICE, useExisting: SharedTranslateService },
    { provide: PRICING_SERVICE, useExisting: SharedPricingService },
    { provide: MOMENT_SERVICE, useExisting: SharedMomentService },
    { provide: BOOKING_TRANSFORMATION_SERVICE, useExisting: SharedBookingTransformationService },
    { provide: DISCOUNT_TRANSFORMER_SERVICE, useExisting: SharedDiscountTransformerService },
    { provide: TIME_INTERVAL_TRANSFORMER_SERVICE, useExisting: SharedTimeIntervalTransformerService },
    { provide: PAYMENT_METHOD_TRANSFORMER_SERVICE, useExisting: SharedPaymentMethodTransformerService },
    // Bootstrap the pieces the wizard needs but the host app may not set up:
    // app-settings + pricing model + translations. Fire-and-forget on purpose —
    // NOT returned — so bootstrap never blocks on it. It must not: pricing and
    // the translation loader both derive from SharedAppSettingsService.$current,
    // and an awaited initializer that hangs (see below) would freeze hydration.
    // Browser-only — the booking stack is a browser concern, never SSR/prerender.
    provideAppInitializer(() => {
      if (!bootstrap || !isPlatformBrowser(inject(PLATFORM_ID))) {
        return;
      }

      // App settings FIRST — everything else depends on $current emitting.
      // $current is a ReplaySubject that stays silent until init() runs; a host
      // like landing never calls it, so it never emits, and anything awaiting it
      // hangs forever: the daily-pricing feature stream (pricing.init) AND the
      // app-text loader's getTranslationFromApi (which does $current.pipe(take(1)))
      // — the latter is why the wizard's i18n keys rendered raw. init() fails
      // OPEN (a failed settings fetch degrades to web defaults and still emits),
      // so it can't hang. A host that initialises settings itself must opt out
      // with `bootstrap: false` (see BookingStackOptions): init() is
      // first-caller-wins, so this call would otherwise claim it.
      const appSettings = inject(SharedAppSettingsService);
      const translate = inject(SharedTranslateService);
      const pricing = inject(SharedPricingService);

      // Base defaults for the wizard's booking-flow flags. The settings API row
      // for `landing` doesn't carry these (they live in web-traveler's
      // DefaultAppSettings, app.settings.ts, not in a DB row), so without a base
      // the wizard would fall back to each resolver's default and diverge from
      // web-traveler — hiding the free-cancellation banner / service-fee note /
      // coverage skip link that production shows. Mirror web-traveler's
      // booking-flow defaults so the wizard behaves identically on landing.
      //
      // `bookingWizardAccumulatedStorageRate: false` is stated explicitly even
      // though it equals the resolver fallback — the point is that this object
      // *is* landing's booking-flow base, so parity is visible here rather than
      // resting on a fallback matching by coincidence.
      //
      // Deliberately NOT set: `bookingWizardPremiumInsuranceEnabled`. web-traveler
      // ships it `true`, but the Premium coverage tier stays OFF on landing for
      // now (resolver fallback `false`) until landing is signed off — omitting it
      // keeps the tier hidden. Adding it here would turn Premium on for landing.
      const wizardDefaultSettings = {
        bookingWizardFreeCancellationBanner: true,
        bookingWizardServiceFeeNote: true,
        bookingWizardCoverageSkipLink: true,
        bookingWizardConfirmNoCoverage: true,
        // `as const`: the literal must stay the union member, not widen to `string`, to satisfy `init`.
        bookingWizardCoverageDefault: 'none' as const,
        bookingWizardAccumulatedStorageRate: false,
      };

      void appSettings
        .init('landing', 'web', wizardDefaultSettings)
        .catch(() => undefined)
        .finally(() => {
          // Pricing: seed is synchronous ('hourly'); this only flips it to
          // 'daily' if the now-emitting feature stream resolves that way.
          void pricing.init().catch(() => undefined);

          // Translations: set English as default + active. currentLang is empty
          // until this runs, so keys render raw; setLanguage triggers the loader
          // (now unblocked) to fetch app_text, with the committed EN seed as a
          // floor. Idempotent — a host that already picked a language re-affirms.
          translate.setDefaultLang('en');
          if (!translate.currentLanguage) {
            translate.setLanguage('en').subscribe({ error: () => undefined });
          }
        });
    }),
  ]);
}
